// Справка „Продажби по периоди" (Справки → sales-by-periods).
//
// Един ред = един отрязък от времето. Групирането (слято по час/ден от
// седмицата/месец или хронологично по час/дата/месец/година) и трите
// филтъра — потребители, клиенти, артикули — са ПАРАМЕТРИ на заявката към
// `GET /reports/analytics/sales-by-periods`; отговорът носи целия период
// наведнъж, затова таблицата е в КЛИЕНТСКИ режим (търсачка, филтри в
// колоните, ред „Общо" — в браузъра). Станциите не са филтър, а колони —
// оборотът на всяка станция в отрязъка (`stations` на реда), изгасени по
// подразбиране в панела „Колони".
//
// Пренесена от старата справка на TouchSale: там имаше и „заведение", „работни
// точки" и разбивка по маса — в новия апп ресторантът е един и те отпадат.

import { useCallback, useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useSalesByPeriodsReport } from "../../../hooks/useReports";
import { useMenu, useMenuStations } from "../../../hooks/useMenu";
import {
  usePaymentMethods,
  useActivePaymentMethods,
} from "../../../hooks/usePaymentMethods";
import {
  buildMethodEntries,
  type MethodEntry,
} from "../../../utils/paymentMethodsDisplay";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import type { SalesByPeriodsFilters as ApiFilters } from "../../../api/reportsSalesByPeriods";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import { SalesByPeriodsFilters } from "./salesByPeriods/SalesByPeriodsFilters";
import {
  EMPTY_FILTERS,
  WITHOUT_CLIENT,
  type ReportFilterState,
} from "./salesByPeriods/reportFilterState";
import { useSalesByPeriodsView } from "./salesByPeriods/useSalesByPeriodsView";
import { PeriodDaysModal } from "./salesByPeriods/PeriodDaysModal";
import { SalesByPeriodsMobileCard } from "./salesByPeriods/SalesByPeriodsMobileCard";
import {
  useSalesByPeriodsColumns,
  type StationColumn,
} from "./salesByPeriods/salesByPeriodsColumns";
import { exportSalesByPeriods } from "./salesByPeriods/salesByPeriodsExport";
import {
  buildPeriodRows,
  methodTotalsOfRows,
  type PeriodRow,
} from "./salesByPeriods/salesByPeriodsMath";

// Bill.payment_method_id на смесеното плащане (BE: PAYMENT_METHOD_MIXED).
const MIXED_PAYMENT_METHOD_ID = 3;

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

const toIds = (values: string[]): number[] =>
  values.map(Number).filter((value) => Number.isFinite(value));

export const SalesByPeriodsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const isBg = !i18n.language?.startsWith("en");
  const langCode = isBg ? "bg" : "en";

  // Периодът е с час: справката отговаря и на „между 18:00 и 23:00".
  const [dateRange, setDateRange] = useReportsDateParam(
    () => [businessDayStartFrom(dayjs()), businessDayEndFrom(dayjs())],
    "business",
  );
  // Режимът и единицата се пазят в localStorage (useSalesByPeriodsView)
  const { mode, unit, changeMode, changeUnit } = useSalesByPeriodsView();
  const [reportFilters, setReportFilters] =
    useState<ReportFilterState>(EMPTY_FILTERS);
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<PeriodRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<PeriodRow | null>(null);

  const filters = useMemo<ApiFilters>(() => {
    const base: ApiFilters = {
      mode,
      unit,
      user_ids: toIds(reportFilters.userIds),
      client_ids: toIds(
        reportFilters.clientIds.filter((value) => value !== WITHOUT_CLIENT),
      ),
      without_client: reportFilters.clientIds.includes(WITHOUT_CLIENT),
      item_ids: toIds(reportFilters.itemIds),
    };
    if (!dateRange?.[0] || !dateRange?.[1]) return base;
    return {
      ...base,
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [mode, unit, reportFilters, dateRange]);

  const { data: report, isLoading, isFetching, refetch } =
    useSalesByPeriodsReport(filters);

  const rows = useMemo(
    () =>
      report
        ? buildPeriodRows(report.rows, report.mode, report.unit, i18n.language)
        : [],
    [report, i18n.language],
  );

  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        row.label.toLowerCase().includes(needle) ||
        (row.sublabel ?? "").toLowerCase().includes(needle),
    );
  }, [rows, searchText]);

  const { data: activeMethods = [] } = useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // Смесеното няма своя колона: BE го разпределя в „В брой" и „С карта", а
  // активният метод „Смесено" иначе би дал празна колона.
  const methodEntries = useMemo<MethodEntry[]>(
    () =>
      buildMethodEntries({
        activeMethods: activeMethods.filter(
          (method) => method.id !== MIXED_PAYMENT_METHOD_ID,
        ),
        allMethods,
        totals: methodTotalsOfRows(searchedRows),
        total: searchedRows.reduce((sum, row) => sum + row.revenue, 0),
        isBg,
      }),
    [activeMethods, allMethods, searchedRows, isBg],
  );

  // Станциите от менюто в неговия ред + станция с оборот, която я няма там
  // (изтрита) — за да не изчезват пари от колоните.
  const menuStations = useMenuStations();
  const { isLoading: menuLoading } = useMenu();
  const stationColumns = useMemo<StationColumn[]>(() => {
    const columns: StationColumn[] = menuStations.map((station) => ({
      id: station.id,
      label:
        station.translations.find(
          (translation) => translation.languageCode === langCode,
        )?.name ??
        station.translations[0]?.name ??
        `#${station.id}`,
    }));
    const known = new Set(columns.map((station) => station.id));
    for (const row of rows) {
      for (const [stationId, amount] of row.stationAmounts) {
        if (known.has(stationId) || !amount) continue;
        known.add(stationId);
        columns.push({ id: stationId, label: `#${stationId}` });
      }
    }
    return columns;
  }, [menuStations, rows, langCode]);

  const columns = useSalesByPeriodsColumns({ methodEntries, stationColumns });

  const handleFilteredRowsChange = useCallback(
    (next: PeriodRow[]) => setVisibleRows(next),
    [],
  );

  const handleExport = () =>
    exportSalesByPeriods({ rows: visibleRows, methodEntries, stationColumns, t });

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.salesPeriodsTableKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.salesPeriodsRowOne",
          "reportsPage.salesPeriodsRowMany",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.salesPeriodsTableHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.salesPeriodsPageTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
          </div>

          <SalesByPeriodsFilters
            mode={mode}
            unit={unit}
            onModeChange={changeMode}
            onUnitChange={changeUnit}
            filters={reportFilters}
            onFiltersChange={setReportFilters}
          />

          {/* Таблицата чака и менюто: колоните по станция трябва да са налице
              при първия рендер, иначе запомнената им видимост се губи. */}
          {isLoading || menuLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <DataTable<PeriodRow>
              tableId="sales-by-periods"
              data={searchedRows}
              columns={columns}
              getRowId={(row) => row.key}
              refreshing={isFetching}
              ariaLabel={t("reportsPage.salesPeriodsPageTitle")}
              title={tableTitle}
              search={{
                value: searchText,
                onChange: setSearchText,
                placeholder: t("reportsPage.salesPeriodsSearchPlaceholder"),
              }}
              onFilteredRowsChange={handleFilteredRowsChange}
              toolbarRight={
                <ExportExcelButton
                  onExport={handleExport}
                  disabled={visibleRows.length === 0}
                  bare
                />
              }
              onRowClick={setSelectedRow}
              mobileCard={(row) => <SalesByPeriodsMobileCard row={row} />}
              initialSorting={[{ id: "period", desc: false }]}
              pageSize={50}
              emptyTitle={t("reportsPage.salesPeriodsEmptyTitle")}
              emptyHint={t("reportsPage.salesPeriodsEmptyHint")}
            />
          )}
        </div>
      </div>

      <PeriodDaysModal row={selectedRow} onClose={() => setSelectedRow(null)} />
    </div>
  );
};
